import * as React from 'react';
import { useSelector } from 'react-redux';
// oxlint-disable-next-line cian-eslint-compat/no-restricted-paths
import { CianGptBannerContainer } from 'shared/containers/CianGptBannerContainer';
import { NewbuildingOffersCarouselContainer } from 'shared/containers/NewbuildingOffersCarouselContainer';
import { IIntegrateAdOptions } from 'shared/containers/OffersContainer';
import { selectCommonCPDLandingExperiment } from 'shared/selectors/experiments/selectCommonCPDLandingExperiment';
import { selectIsMixedListing, selectIsNewbuildingOrMixedListing } from 'shared/selectors/listingType';
import { selectIsMortgageRateHikeBannerClosed } from 'shared/selectors/mortgageRateHikeBanner';
import { getIsNewbuildingBannerRotationEnabled } from 'shared/selectors/newbuildingBannerRotation';
import { selectNewbuildingBrokerConsultantBannerAvailable } from 'shared/selectors/newbuildingBrokerConsultantBanner';
import { selectIsNewbuildingOffersWithBannerCarouselVisible } from 'shared/selectors/newbuildingOffersCarousel';
import { selectRandomBoolean } from 'shared/selectors/randomBoolean';
import { CommonCPDLandingBannerContainer } from 'shared/serp/components/CommonCPDLandingBanner';
import { NewbuildingBrokerConsultantBannerContainer } from 'shared/serp/components/NewbuildingBrokerConsultantBanner';
import { DailyrentBannerContainer } from 'shared/serp/containers/DailyrentBannerContainer';
import { MortgageRateHikeBannerContainer } from 'shared/serp/containers/MortgageRateHikeBannerContainer';
import { NewbuildingCompareFlightBannerContainer } from 'shared/serp/containers/NewbuildingCompareFlightBannerContainer';
// oxlint-disable-next-line cian-eslint-compat/no-restricted-paths
import { removeCookie } from '../../../../browser/utils/js-cookie';
import { EBannerType } from '../../../constants/bannerType';
import { DiscountMortgageBannerContainer } from '../../../containers/NewbuildingDiscountMortgageBanner';
import { getDealType } from '../../../selectors/getDealType';
import { getJsonQuery } from '../../../selectors/getJsonQuery';
import { getNewbuildingSimilarAdPath } from '../../../selectors/getNewbuildingSimilarAdPath';
import { getNewbuildingSimilarOffers } from '../../../selectors/getNewbuildingSimilarOffers';
import { getOfferType } from '../../../selectors/getOfferType';
import { selectBannerTypeRotation } from '../../../selectors/selectBannerTypeRotation';
import { selectIsCommercial } from '../../../selectors/selectIsCommercial';
import { selectSuburbanBlackFridayBannerUrl } from '../../../selectors/suburbanBlackFriday';
import { EDealType } from '../../../types/dealType';
import { useApplicationContext } from '../../../utils/applicationContext';
import { FOfferType, isSuburban } from '../../../utils/category';
import { getNewbuildingBannerTestCookie } from '../../../utils/getNewbuildingBannerTestCookie';
import { getNewbuildingEntryBannerInfo } from '../../../utils/getNewbuildingEntryBannerInfo';
import {
  NewbuildingEntryResaleBlock,
  trackNewbuildingEntryResaleBlockShow,
  trackNewbuildingEntryResaleBlockButtonClick,
  trackNewbuildingEntryResaleBlockItemClick,
} from '../../components/NewbuildingEntryResaleBlock';
import { SuburbanBlackFridayBannerContainerLoadable } from '../../components/SuburbanBlackFridayBanner';
import { SuburbanBuildersProjectsBanner } from '../../components/SuburbanBuildersProjectsBanner';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { SubsidisedMortgageBannerContainer } from '../SubsidisedMortgageBannerContainer';
import { CPD1000BannerContainer } from '../CPD1000BannerContainer';

/**
 * Позиция первого сервисного баннерного слота на первой странице (после 3-го объявления).
 * При активной карусели новостроек BannerContainer в тот же слот приходит из getCarouselBanner с position === 1.
 */
const FIRST_PAGE_SERVICES_BANNER_POSITION = 2;
const FIRST_PAGE_CAROUSEL_BANNER_POSITION = 1;

export interface IBannerConatinerProps {
  options: IIntegrateAdOptions;
  position: number;
  isAgent?: boolean;
  locationId?: number;
  isCountrysideBuildersBannerAvailable?: boolean;
  isVisibleNewbuildingCarousel?: boolean;
}

export function BannerContainer({
  options,
  position,
  isAgent,
  isCountrysideBuildersBannerAvailable,
  isVisibleNewbuildingCarousel,
}: IBannerConatinerProps) {
  const isNewbuildingBannerRotationEnabled = useSelector(getIsNewbuildingBannerRotationEnabled);
  const bannerTypeRotation = useSelector(selectBannerTypeRotation);
  const isNewbuildingOrMixedListing = useSelector(selectIsNewbuildingOrMixedListing);
  const isMixedListing = useSelector(selectIsMixedListing);
  const isCommonCPDLandingBannerExperiment = useSelector(selectCommonCPDLandingExperiment);
  const isNewbuildingBrokerConsultantBannerAvailable = useSelector(selectNewbuildingBrokerConsultantBannerAvailable);
  const dealType = useSelector(getDealType);
  const offerType = useSelector(getOfferType);
  const jsonQuery = useSelector(getJsonQuery);
  const isCommercialListing = useSelector(selectIsCommercial);
  const nbSimilarOffers = useSelector(getNewbuildingSimilarOffers);
  const nbSimilarAdPath = useSelector(getNewbuildingSimilarAdPath);
  const { logger, config } = useApplicationContext();
  const isDiscountMortgageBannerEnabled = config.get<boolean>('newbuildingDiscountMortgageBanner.Enabled');
  const isMortgageRateHikeBannerEnabled = config.get<boolean>('Newbuilding.MortgageRateHikeBanner.Enabled') || false;
  const isMortgageRateHikeBannerClosed = useSelector(selectIsMortgageRateHikeBannerClosed);
  const isCompareFlightBannerEnabled = config.get<boolean>('Newbuilding.CompareFlightTrap.Enabled') || false;
  const suburbanBlackFridayUrl = useSelector(selectSuburbanBlackFridayBannerUrl);
  const isCianGptBannerEnabled = config.get<boolean>('audience.cianGPTFlight');

  const randomBoolean = useSelector(selectRandomBoolean);
  const isNewbuildingOffersWithBannerCarouselVisible = useSelector(selectIsNewbuildingOffersWithBannerCarouselVisible);

  const [hideCarouselBanner, setHideCarouselBanner] = React.useState(false);
  const prevJsonQueryRef = React.useRef(jsonQuery);

  // Реанимируем карусель при изменении jsonQuery, если условия снова подходят для отображения
  React.useEffect(() => {
    const hasJsonQueryChanged = prevJsonQueryRef.current !== jsonQuery;

    if (hasJsonQueryChanged && hideCarouselBanner) {
      const shouldShowCarousel =
        isNewbuildingOffersWithBannerCarouselVisible &&
        !options.suggestionFragment?.isSimilar &&
        isVisibleNewbuildingCarousel;

      if (shouldShowCarousel) {
        setHideCarouselBanner(false);
      }
    }

    prevJsonQueryRef.current = jsonQuery;
  }, [
    jsonQuery,
    hideCarouselBanner,
    isNewbuildingOffersWithBannerCarouselVisible,
    options.suggestionFragment?.isSimilar,
    isVisibleNewbuildingCarousel,
  ]);

  const rotationBanner = React.useMemo(() => {
    switch (bannerTypeRotation) {
      case EBannerType.MortgageBroker:
        return <MortgageBrokerBannerContainer />;
      case EBannerType.SubsidisedMortgage:
        return <SubsidisedMortgageBannerContainer />;
      // CD-278800 Баннер посуточной аренды сейчас выключен (флаг DailyRent.ListingDesktopBanner.Enabled)
      case EBannerType.DailyRent:
        return <DailyrentBannerContainer />;
      case EBannerType.CPD1000:
        return <CPD1000BannerContainer />;
      default:
        return null;
    }
  }, [bannerTypeRotation]);

  React.useEffect(
    /* istanbul ignore next */ () => {
      if (!isCountrysideBuildersBannerAvailable) {
        removeCookie('buildersBannerShown');
      }
    },
    [isCountrysideBuildersBannerAvailable],
  );

  /**
   * Сервисные баннеры первого слота первой страницы: 50% загрузок (!randomBoolean), новостроечная или смешанная
   * выдача. Стоят выше карусели и новостроечного слота, чтобы вероятность показа совпадала с мобильной выдачей.
   * Баннер флайта сравнения ЖК приоритетнее баннера об изменениях в ипотечных программах.
   * QA-кука newbuilding_search_banner_test=broker отключает сервисные баннеры, чтобы в слот попал баннер брокера.
   */
  const isFirstPageBannerSlot = isVisibleNewbuildingCarousel
    ? position === FIRST_PAGE_CAROUSEL_BANNER_POSITION
    : position === FIRST_PAGE_SERVICES_BANNER_POSITION;
  const isBrokerBannerTest = getNewbuildingBannerTestCookie() === 'broker';

  if (
    isFirstPageBannerSlot &&
    !isBrokerBannerTest &&
    !randomBoolean &&
    isNewbuildingOrMixedListing &&
    !isAgent &&
    !options.suggestionFragment?.isSimilar
  ) {
    if (isCompareFlightBannerEnabled) {
      return <NewbuildingCompareFlightBannerContainer />;
    }

    if (isMortgageRateHikeBannerEnabled && !isMortgageRateHikeBannerClosed) {
      return <MortgageRateHikeBannerContainer />;
    }
  }

  if (
    isNewbuildingOffersWithBannerCarouselVisible &&
    !hideCarouselBanner &&
    !options.suggestionFragment?.isSimilar &&
    isVisibleNewbuildingCarousel
  ) {
    return (
      <NewbuildingOffersCarouselContainer
        onError={() => setHideCarouselBanner(true)}
        onNoData={() => setHideCarouselBanner(true)}
      />
    );
  }

  if (
    position === 2 &&
    randomBoolean &&
    isCianGptBannerEnabled &&
    [FOfferType.Flat, FOfferType.FlatOld].includes(offerType)
  ) {
    return <CianGptBannerContainer />;
  }

  // Определяем, нужно ли показывать новостроечный баннер в новостроечной и смешанной выдаче
  if (isNewbuildingBannerRotationEnabled && !isAgent && isNewbuildingOrMixedListing) {
    // TODO: В CD-266903 при выигрыше экспа переименовать переменную, селектор, имя директории для isNewbuildingBannerRotationEnabled,
    // а также обновить коммент выше. По смыслу эта переменная - новостроечный слот для баннеров, а не баннер (isNewbuildingSlotActive).
    if (isMixedListing && isCommonCPDLandingBannerExperiment) {
      return <CommonCPDLandingBannerContainer />;
    }

    if (isDiscountMortgageBannerEnabled) {
      return <DiscountMortgageBannerContainer />;
    }

    if (isNewbuildingBrokerConsultantBannerAvailable) {
      return <NewbuildingBrokerConsultantBannerContainer />;
    }

    return null;
  }

  // Определяем, нужно ли показывать блоки объявлений из новостроек в выдаче квартир вторички
  if (
    isNewbuildingBannerRotationEnabled &&
    nbSimilarOffers &&
    nbSimilarAdPath &&
    offerType === FOfferType.FlatOld &&
    dealType === EDealType.Sale
  ) {
    const { title, description } = getNewbuildingEntryBannerInfo('resale', config, logger);

    return (
      <NewbuildingEntryResaleBlock
        onShow={trackNewbuildingEntryResaleBlockShow(jsonQuery)}
        onButtonClick={trackNewbuildingEntryResaleBlockButtonClick(jsonQuery)}
        onItemClick={trackNewbuildingEntryResaleBlockItemClick(jsonQuery)}
        url={nbSimilarAdPath}
        title={title}
        description={description}
        items={nbSimilarOffers}
      />
    );
  }

  // Если в загородке в продаже акция "Черная пятница" активна, то показываем баннер
  if (isSuburban(offerType) && dealType === EDealType.Sale && suburbanBlackFridayUrl) {
    return <SuburbanBlackFridayBannerContainerLoadable href={suburbanBlackFridayUrl} />;
  }

  if (randomBoolean && (offerType & FOfferType.Land) !== 0 && dealType === EDealType.Sale) {
    return <SuburbanBuildersProjectsBanner />;
  }

  if (rotationBanner && !isCommercialListing) {
    return rotationBanner;
  }

  return null;
}
